<template>
    <div
        class="filter text-body-2"
        :class="{ search: filter.type == 'search', embedded: embedded }"
        :key="filterName"
    >
    <template v-if="filter.type == 'search' || !filter.type">
            <v-text-field
                :autofocus="filter.focus"
                :background-color="backgroundColor"
                :class="{ active: isActive }"
                :disabled="filter.disabled"
                :label="filter.label"
                :placeholder="placeHolder"
                :prepend-inner-icon="iconName('search')"
                :value="value"
                @input="$emit('change', $event || null)"
                clearable
                dense
                hide-details
                outlined
                single-line
            />
        </template>
        <template v-else-if="filter.type == 'country'">
            <CountrySelect
                ref="dropdownControl"
                :value="value"
                :background-color="backgroundColor"
                :class="{ active: isActive, filter: true }"
                :clearable="!filter.defaultLabel && filter.clearable != false"
                :disabled="filter.disabled"
                :items="choices | flattenChoices"
                :label="filterLabel"
                :loading="loading"
                :prepend-inner-icon="filterIcon(filter.icon)"
                :required="filter.required"
                @input="$emit('change', $event || null)"
                :dense="true"
                :flat="true"
                :hide-details="true"
                :outlined="true"
            />
        </template>
        <template v-else-if="['float', 'integer'].indexOf(filter.type) != -1">
            <v-combobox
                ref="dropdownControl"
                :autofocus="embeddedAutofocus"
                :background-color="backgroundColor"
                :class="{ active: isActive }"
                :clearable="!filter.defaultLabel && filter.clearable != false"
                :disabled="filter.disabled"
                :items="choices | flattenChoices"
                :label="filterLabel"
                :loading="loading"
                :menu-props="dropdownMenuProps"
                :prepend-inner-icon="filterIcon(filter.icon)"
                :required="filter.required"
                :value="value"
                @input="$emit('change', $event || null)"
                type="number"
                dense
                flat
                hide-details
                outlined
            />
        </template>
        <template v-else-if="filter.type == 'input' || filter.type == 'string'">
            <v-combobox
                ref="dropdownControl"
                :autofocus="embeddedAutofocus"
                :background-color="backgroundColor"
                :class="{ active: isActive }"
                :clearable="!filter.defaultLabel && filter.clearable != false"
                :disabled="filter.disabled"
                :items="choices | flattenChoices"
                :label="filter.label"
                :loading="loading"
                :menu-props="dropdownMenuProps"
                :prepend-inner-icon="filterIcon(filter.icon)"
                :required="filter.required"
                :value="value"
                @input="$emit('change', $event)"
                class="autocomplete"
                dense
                flat
                hide-details
                outlined
            />
        </template>
        <template v-else-if="filter.type == 'boolean'">
            <v-checkbox
                :background-color="backgroundColor"
                :class="{ active: isActive }"
                :disabled="filter.disabled"
                :input-value="!!value || false"
                :key="value"
                :label="filter.label"
                :name="filterName"
                :prepend-inner-icon="filterIcon(filter.icon)"
                @change="booleanInputChanged"
                class="text-body-2 mt-0 checkbox"
                dense
                hide-details
                outlined
                single-line
            >
            </v-checkbox>
        </template>
        <template v-else-if="filter.type == 'formatselect'">
            <FormatSelect
                ref="dropdownControl"
                :autofocus="embeddedAutofocus"
                :value="value"
                :formats="formatRecords"
                :label="filter.label"
                :loading="loading"
                variant="grouped"
                :show-custom-option="false"
                :show-create-option="false"
                clearable
                dense
                outlined
                hide-details
                :class="['format-filter-select', { active: isActive }]"
                @input="$emit('change', $event || null)"
            />
        </template>
        <template v-else-if="filter.type == 'select' && filter.multiple">
            <v-autocomplete
                ref="dropdownControl"
                :autofocus="embeddedAutofocus"
                :background-color="backgroundColor"
                :class="{ active: isActive }"
                :clearable="!filter.defaultLabel && filter.clearable != false"
                :disabled="filter.disabled"
                :items="choices"
                :label="filter.label"
                :loading="loading"
                :menu-props="dropdownMenuProps"
                :multiple="filter.multiple"
                :prepend-inner-icon="filterIcon(filter.icon)"
                :required="filter.required"
                :value="value"
                @input="$emit('change', $event)"
                class="autocomplete"
                :no-data-text="$translate('noDataAvailable')"
                dense
                flat
                hide-details
                outlined
            >
                <template v-slot:selection="{ index, item }">
                    <template v-if="index == 0">
                        <template v-if="filter.multiple">
                            <span v-if="value?.length > 1" style="font-size: 9pt">
                                {{ $translate("items").replace("{count}", nbItems) }}
                            </span>
                            <template v-else>
                                <span style="font-size: 9pt">
                                    {{ item.text }}
                                </span>
                            </template>
                        </template>
                        <template v-else>
                            <span style="font-size: 9pt">
                                {{ item.text }}
                            </span>
                        </template>
                    </template>
                </template>
            </v-autocomplete>
        </template>
        <template v-else-if="filter.type == 'combo' || (filter.type == 'select' && !filter.multiple)">
            <v-select
                ref="dropdownControl"
                :autofocus="embeddedAutofocus"
                :background-color="backgroundColor"
                :class="{ active: isActive }"
                :clearable="!filter.defaultLabel && filter.clearable != false"
                :disabled="filter.disabled"
                :items="choices"
                :label="filter.label"
                :loading="loading"
                :menu-props="dropdownMenuProps"
                :multiple="filter.multiple"
                :prepend-inner-icon="filterIcon(filter.icon)"
                :required="filter.required"
                :value="value"
                item-text="text"
                item-value="value"
                @input="$emit('change', $event)"
                class="combobox"
                dense
                flat
                hide-details
                outlined
        /></template>
        <template v-else-if="filter.type == 'searchselect'">
            <v-autocomplete
                ref="dropdownControl"
                :autofocus="embeddedAutofocus"
                :background-color="backgroundColor"
                :class="{ active: isActive }"
                :clearable="!filter.defaultLabel && filter.clearable != false"
                :disabled="filter.disabled"
                :items="choices"
                :label="filter.label"
                :placeholder="filter.placeholder || undefined"
                :loading="loading"
                :menu-props="dropdownMenuProps"
                :prepend-inner-icon="filterIcon(filter.icon)"
                :required="filter.required"
                :search-input="search"
                :value="value"
                item-text="text"
                item-value="value"
                @input="$emit('change', $event)"
                @update:search-input="updateSearch"
                class="autocomplete"
                dense
                flat
                hide-details
                outlined
        /></template>
        <template v-else-if="filter.type == 'searchprovider'">
            <SearchProvider
                ref="dropdownControl"
                :value="value"
                :background-color="backgroundColor"
                :class="{ active: isActive }"
                flat
                dense
                outlined
                @input="$emit('change', $event?.id)"
                :label="filter.label"
                :disabled="filter.disabled"
                :icon="filter.icon"
                :loading="loading"
                :hide-details="true"
                :autofocus="embeddedAutofocus"
                :load-on-mount="embedded"
                :menu-props="dropdownMenuProps"
            />
        </template>
        <template v-else-if="filter.type == 'searchcustomer'">
            <SearchCustomer
                ref="dropdownControl"
                :value="value"
                :background-color="backgroundColor"
                :class="{ active: isActive }"
                flat
                dense
                outlined
                @input="$emit('change', $event?.id)"
                :label="filter.label"
                :disabled="filter.disabled"
                :icon="filter.icon"
                :loading="loading"
                :hide-details="true"
                :autofocus="embeddedAutofocus"
                :load-on-mount="embedded"
                :menu-props="dropdownMenuProps"
            />
        </template>
        <template v-else-if="filter.type == 'datetime'">
            <DateTime
                :autofocus="filter.focus"
                :background-color="backgroundColor"
                :class="{ active: isActive }"
                :clearable="filter.clearable != false"
                :dense="true"
                :disabled="filter.disabled"
                :fixed="false"
                :flat="true"
                :label="filter.label"
                :loading="loading"
                :min-date="null"
                :outlined="true"
                :prepend-inner-icon="filterIcon(filter.icon || 'date')"
                :required="filter.required"
                :value="value"
                @input="$emit('change', $event)"
                class="autocomplete"
                :hide-details="true"
            />
        </template>
        <template v-else-if="filter.type == 'daterange'">
            <v-menu
                v-model="rangeMenu"
                :close-on-content-click="false"
                offset-y
                transition="scale-transition"
            >
                <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                        v-bind="attrs"
                        v-on="on"
                        :background-color="hasRange ? 'primary-lighter2' : 'secondary'"
                        :class="{ active: hasRange }"
                        dense
                        hide-details
                        outlined
                        class="text-body-2"
                        :prepend-inner-icon="filterIcon(filter.icon || 'date')"
                        :value="rangeLabel"
                        :label="filter.label"
                        :placeholder="filter.placeholder || filter.label"
                        clearable
                        @click:clear="clearRange"
                    />

                </template>
                <v-card>
                    <div
                        v-if="filter.dateRangePresets"
                        class="filter__date-range-presets"
                    >
                        <v-btn
                            v-for="code in datePresetChoices"
                            :key="code"
                            x-small
                            text
                            :class="[
                                'text-none filter__date-range-preset',
                                {
                                    'filter__date-range-preset--active':
                                        selectedPreset === code,
                                },
                            ]"
                            @click="selectPreset(code)"
                        >
                            {{ datePresetLabel(code) }}
                        </v-btn>
                    </div>
                    <v-date-picker
                        v-model="pickerRange"
                        range
                        color="primary"
                        scrollable
                        no-title
                    />
                    <v-divider />
                    <v-card-actions>
                        <v-spacer />
                        <v-btn small text @click="cancelRange">
                            {{ $translate("dateRangeClose") }}
                        </v-btn>
                        <v-btn
                            color="primary"
                            small
                            text
                            :disabled="!isRangeComplete"
                            @click="applyRange"
                        >
                            {{ $translate("dateRangeApply") }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-menu>
        </template>
        <template v-else-if="filter.type == 'numberrange'">
            <v-menu
                v-model="rangeMenu"
                :close-on-content-click="false"
                offset-y
                transition="scale-transition"
            >
                <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                        v-bind="attrs"
                        v-on="on"
                        :background-color="hasRange ? 'primary-lighter2' : 'secondary'"
                        :class="{ active: hasRange }"
                        dense
                        hide-details
                        outlined
                        class="text-body-2"
                        :prepend-inner-icon="filterIcon(filter.icon || 'euro')"
                        :value="numberRangeLabel"
                        :label="filter.label"
                        :placeholder="filter.placeholder || filter.label"
                        clearable
                        @click:clear="clearRange"
                    />
                </template>
                <v-card class="pa-4" min-width="280">
                    <v-row dense>
                        <v-col cols="6">
                            <v-text-field
                                v-model="pickerMin"
                                type="text"
                                inputmode="decimal"
                                dense
                                outlined
                                hide-details
                                :label="$translate('numberRangeMin')"
                                :suffix="filter.unit"
                            />
                        </v-col>
                        <v-col cols="6">
                            <v-text-field
                                v-model="pickerMax"
                                type="text"
                                inputmode="decimal"
                                dense
                                outlined
                                hide-details
                                :label="$translate('numberRangeMax')"
                                :suffix="filter.unit"
                            />
                        </v-col>
                    </v-row>
                    <v-divider />
                    <v-card-actions class="px-0 pb-0">
                        <v-spacer />
                        <v-btn small text @click="cancelRange">
                            {{ $translate("dateRangeClose") }}
                        </v-btn>
                        <v-btn
                            color="primary"
                            small
                            text
                            :disabled="!hasNumberRangeInput"
                            @click="applyNumberRange"
                        >
                            {{ $translate("dateRangeApply") }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-menu>
        </template>
    </div>
</template>

<script>
import Vue from "vue";
import axios from "axios";
import GlobalMixins from "@/mixins/global";
import { formatProductChoiceLabel } from "@/utils/apiFilterParams";
import { buildCatalogChoice } from "@/utils/filterChoices";
import {
    DATE_RANGE_PRESETS,
    activeDateRangePreset,
    dateRangePresetLabel,
    resolveDateRangePreset,
} from "@/utils/dateRangePresets";

export default {
    mixins: [GlobalMixins],
    props: {
        value: {
            required: false,
            default: null,
        },
        filter: {
            type: Object,
            required: false,
            default: () => {},
        },
        filters: {
            type: Object,
            required: false,
            default: () => {},
        },
        embedded: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: false,
            source_choices: null,
            formatRecords: [],
            search: null,
            value_label: null,
            rangeMenu: false,
            pickerRange: [],
            pickerMin: "",
            pickerMax: "",
            selectedPreset: null,
            suppressPickerEmit: false,
        };
    },

    filters: {
        flattenChoices(choices) {
            if (!choices) {
                return [];
            }
            return choices.map((item) => item.value);
        },
    },
    watch: {
        filters: {
            handler(newFilters) {
                // For daterange filters, reconstruct pickerRange from min/max values in filters
                if (this.filter.type === "daterange") {
                    this.initializeDateRange();
                } else if (this.filter.type === "numberrange") {
                    this.initializeNumberRange();
                }
            },
            deep: true,
            immediate: true,
        },
        value: {
            handler(value) {
                if (this.filter.type === "formatselect") {
                    const format = this.formatRecords.find(
                        (item) => item.id == value,
                    );
                    this.value_label = format
                        ? this.$utils.formatOptionLabel(format)
                        : null;
                    return;
                }
                // Keep label for select/searchselect in sync
                this.value_label = this.choices?.find(
                    (item) => item.value == this.value
                )?.text;
                // For daterange filters, the filters watcher handles pickerRange initialization
                // No need to duplicate the logic here
            },
        },
        pickerRange: {
            handler(newRange) {
                if (this.filter.type !== "daterange") {
                    return;
                }

                // Setting pickerRange from a preset selection must not emit
                // a manual {min, max} payload: consume the guard and stop.
                if (this.suppressPickerEmit) {
                    this.suppressPickerEmit = false;
                    return;
                }

                // If range is complete and inverted, reverse it
                if (
                    Array.isArray(newRange) &&
                    newRange.length === 2 &&
                    newRange[0] &&
                    newRange[1] &&
                    newRange[0] > newRange[1]
                ) {
                    // Reverse the range to ensure start <= end
                    this.pickerRange = [newRange[1], newRange[0]];
                    return;
                }

                // Auto-apply when both dates are selected
                if (
                    Array.isArray(newRange) &&
                    newRange.length === 2 &&
                    newRange[0] &&
                    newRange[1]
                ) {
                    this.applyRangeWithoutClosing();
                }
            },
            immediate: false,
        },
    },
    computed: {
        embeddedAutofocus() {
            return this.embedded || this.filter.focus;
        },
        dropdownMenuProps() {
            if (this.embedded) {
                return {
                    attach: true,
                    offsetY: true,
                    maxHeight: 280,
                    closeOnClick: false,
                    closeOnContentClick: false,
                    zIndex: 999,
                };
            }
            return { closeOnClick: true };
        },
        nbItems() {
            if (this.filter.multiple) {
                return this.value?.length || 0;
            }
            return 1;
        },
        backgroundColor() {
            return this.isActive ? 'primary-lighter2' : 'secondary';
        },
        isActive() {
            switch (this.filter.type) {
                case 'daterange':
                case 'numberrange':
                    return this.hasRange;
                case 'combo':
                case 'select':
                    if (this.filter.multiple) {
                        return this.value?.length > 0;
                    }
                    return this.value !== null;
                case 'boolean':
                    return this.value;
                default:
                    return !!this.value;
            }
        },
        filterLabel() {
            let filterLabel = this.filter.label;
            if (this.filter.unit) {
                return `${filterLabel} (${this.filter.unit})`;
            }
            return filterLabel;
        },
        placeHolder() {
            return this.filter.placeholder || this.filter.label;
        },
        filterName() {
            return this.filter.field || this.filter.source;
        },
        datePresetChoices() {
            const configured = this.filter.dateRangePresets;
            if (Array.isArray(configured) && configured.length) {
                return configured;
            }
            return DATE_RANGE_PRESETS;
        },
        choices() {
            let choices = [];
            let source_choices = this.source_choices;
            if (this.filter.choices) {
                if (typeof this.filter.choices === "function") {
                    if (!source_choices) {
                        return [];
                    }
                    choices = this.filter.choices(source_choices);
                } else {
                    choices = this.filter.choices;
                }
                if (this.filter.defaultLabel) {
                    choices.insert(0, {
                        value: null,
                        text: this.filter.defaultLabel,
                    });
                }
                if (this.filter.sort) {
                    choices.sort((a, b) =>
                        (a.text || "").toLowerCase().localeCompare((b.text || "").toLowerCase())
                    );
                }
                return choices;
            }
            if (this.filter.transform) {
                source_choices = this.filter.transform(source_choices);
            }
            if (this.value && this.value_label && this.filter.type == "searchselect") {
                if (!source_choices) {
                    return [{ text: this.value_label, value: this.value }];
                }
            }
            return source_choices;
        },
        hasRange() {
            // For daterange filters, check both pickerRange and filters
            // (pickerRange is more immediate, filters may have a delay)
            if (
                this.filter.type === "daterange" ||
                this.filter.type === "numberrange"
            ) {
                if (this.filter.type === "daterange" && this.selectedPreset) {
                    return true;
                }
                if (this.filter.type === "numberrange") {
                    if (this.hasNumberRangeInput) {
                        return true;
                    }
                } else if (
                    Array.isArray(this.pickerRange) &&
                    this.pickerRange.length === 2 &&
                    (this.pickerRange[0] || this.pickerRange[1])
                ) {
                    return true;
                }
                const minField = `${this.filter.field}_min`;
                const maxField = `${this.filter.field}_max`;
                return !!(this.filters[minField] || this.filters[maxField]);
            }
            return Array.isArray(this.pickerRange) && this.pickerRange.length === 2;
        },
        hasNumberRangeInput() {
            return (
                this.parseNumberInput(this.pickerMin) !== null ||
                this.parseNumberInput(this.pickerMax) !== null
            );
        },
        numberRangeLabel() {
            if (this.filter.type !== "numberrange") {
                return "";
            }
            const minField = `${this.filter.field}_min`;
            const maxField = `${this.filter.field}_max`;
            let min = this.parseNumberInput(this.pickerMin);
            let max = this.parseNumberInput(this.pickerMax);
            if (min === null && this.filters[minField] !== undefined) {
                min = Number(this.filters[minField]);
            }
            if (max === null && this.filters[maxField] !== undefined) {
                max = Number(this.filters[maxField]);
            }
            return this.formatNumberRangeLabel(min, max);
        },
        isRangeComplete() {
            return this.hasRange && this.pickerRange[0] && this.pickerRange[1];
        },
        rangeLabel() {
            if (this.filter.type === 'daterange') {
                if (this.selectedPreset) {
                    return this.datePresetLabel(this.selectedPreset);
                }
                // Prefer pickerRange for immediate feedback
                if (Array.isArray(this.pickerRange) && this.pickerRange.length === 2 && (this.pickerRange[0] || this.pickerRange[1])) {
                    const [start, end] = this.pickerRange;
                    if (start && end) {
                        return this.$utils.formatDateRangeLabel(start, end);
                    } else if (start || end) {
                        return this.$utils.formatDateRangeLabel(start || end, end || start);
                    }
                }

                // Fallback to filters
                const minField = `${this.filter.field}_min`;
                const maxField = `${this.filter.field}_max`;
                const min = this.filters[minField];
                const max = this.filters[maxField];

                if (min || max) {
                    return this.$utils.formatDateRangeLabel(min || max, max || min);
                }

                return "";
            }

            if (!this.isRangeComplete) {
                return "";
            }
            const [start, end] = this.pickerRange;
            return this.$utils.formatDateRangeLabel(
                start,
                end,
            );
        },
    },
    methods: {
        openMenu() {
            if (!this.embedded) {
                return;
            }
            if (this.filter.type === "daterange" || this.filter.type === "numberrange") {
                this.rangeMenu = true;
                return;
            }
            const control = this.$refs.dropdownControl;
            if (!control) {
                return;
            }
            if (typeof control.openMenu === "function") {
                control.openMenu();
                return;
            }
            this.openVuetifyDropdown(control);
        },
        openVuetifyDropdown(control) {
            if (typeof control.focus === "function") {
                control.focus();
            }
            this.$nextTick(() => {
                if (typeof control.activateMenu === "function") {
                    control.activateMenu();
                } else if (Object.prototype.hasOwnProperty.call(control, "isMenuActive")) {
                    control.isMenuActive = true;
                }
            });
        },
        initializeDateRange() {
            if (this.filter.type !== 'daterange') {
                return;
            }

            const presetCode = activeDateRangePreset(this.filters, this.filter.field);
            if (presetCode) {
                this.selectedPreset = presetCode;
                const range = resolveDateRangePreset(presetCode);
                const newRange = range ? [range.min, range.max] : [];
                const currentRange = this.pickerRange || [];
                const rangeChanged =
                    newRange.length !== currentRange.length ||
                    newRange[0] !== currentRange[0] ||
                    newRange[1] !== currentRange[1];
                if (rangeChanged) {
                    this.suppressPickerEmit = true;
                    this.pickerRange = newRange;
                }
                return;
            }

            // No preset active: manual date range (or empty).
            this.selectedPreset = null;
            const minField = `${this.filter.field}_min`;
            const maxField = `${this.filter.field}_max`;
            const min = this.filters[minField];
            const max = this.filters[maxField];

            // Calculate the new range
            let newRange;
            if (min || max) {
                newRange = [min || max, max || min];
            } else {
                newRange = [];
            }

            // Only update if the range has actually changed to avoid triggering watchers unnecessarily
            const currentRange = this.pickerRange || [];
            const rangeChanged =
                newRange.length !== currentRange.length ||
                newRange[0] !== currentRange[0] ||
                newRange[1] !== currentRange[1];

            if (rangeChanged) {
                this.pickerRange = newRange;
            }
        },
        datePresetLabel(code) {
            return dateRangePresetLabel(this, code);
        },
        selectPreset(code) {
            if (this.filter.type !== "daterange") {
                return;
            }
            const range = resolveDateRangePreset(code);
            if (!range) {
                return;
            }
            this.selectedPreset = code;
            this.suppressPickerEmit = true;
            this.pickerRange = [range.min, range.max];
            this.$emit("change", { preset: code });
        },
        updateSearch(search) {
            if (this.source_choices) {
                if (
                    this.source_choices.find((item) => {
                        item.text == search;
                    }) != -1
                ) {
                    this.search = null;
                    return;
                }
            }
            this.search = search;
            this.fetchSource();
        },
        filterIcon(icon) {
            return this.iconName(icon || "search");
        },
        booleanInputChanged(value) {
            if (value) {
                this.$emit("change", this.filter.value || true);
            } else {
                if (this.filter.force_value) {
                    this.$emit("change", false);
                } else {
                    this.$emit("change", null);
                }
            }
        },
        clearRange() {
            if (this.filter.type === "numberrange") {
                this.pickerMin = "";
                this.pickerMax = "";
            } else {
                this.pickerRange = [];
                this.selectedPreset = null;
            }
            this.rangeMenu = false;
            // Emit null so parent can clear *_min and *_max
            this.$emit("change", null);
        },
        parseNumberInput(value) {
            if (value === null || value === undefined || value === "") {
                return null;
            }
            const parsed = Number(String(value).replace(",", "."));
            return Number.isFinite(parsed) ? parsed : null;
        },
        formatNumberRangeLabel(min, max) {
            if (min !== null && max !== null) {
                return `${this.$utils.price(min)} – ${this.$utils.price(max)}`;
            }
            if (min !== null) {
                return `≥ ${this.$utils.price(min)}`;
            }
            if (max !== null) {
                return `≤ ${this.$utils.price(max)}`;
            }
            return "";
        },
        initializeNumberRange() {
            if (this.filter.type !== "numberrange") {
                return;
            }
            const minField = `${this.filter.field}_min`;
            const maxField = `${this.filter.field}_max`;
            const min = this.filters[minField];
            const max = this.filters[maxField];
            this.pickerMin =
                min === null || min === undefined ? "" : String(min);
            this.pickerMax =
                max === null || max === undefined ? "" : String(max);
        },
        applyNumberRange() {
            let min = this.parseNumberInput(this.pickerMin);
            let max = this.parseNumberInput(this.pickerMax);
            if (min === null && max === null) {
                return;
            }
            if (min !== null && max !== null && min > max) {
                [min, max] = [max, min];
            }
            const minField = `${this.filter.field}_min`;
            const maxField = `${this.filter.field}_max`;
            const payload = { min, max };
            if (
                this.filters[minField] === payload.min &&
                this.filters[maxField] === payload.max
            ) {
                this.rangeMenu = false;
                return;
            }
            this.$emit("change", payload);
            this.rangeMenu = false;
        },
        cancelRange() {
            this.rangeMenu = false;
        },
        applyRange() {
            // A preset is active: keep it as-is, just close the menu.
            // Switching to fixed dates happens only when the user manually
            // picks dates in the calendar (see applyRangeWithoutClosing).
            if (this.filter.type === "daterange" && this.selectedPreset) {
                this.rangeMenu = false;
                return;
            }
            if (!this.isRangeComplete) {
                return;
            }
            const [start, end] = this.pickerRange;
            const payload = {
                min: start,
                max: end,
            };

            // For daterange filters, check if the range is already applied in filters
            if (this.filter.type === 'daterange') {
                this.selectedPreset = null;
                const minField = `${this.filter.field}_min`;
                const maxField = `${this.filter.field}_max`;
                if (
                    this.filters[minField] === payload.min &&
                    this.filters[maxField] === payload.max
                ) {
                    // Range already applied, just close the menu
                    this.rangeMenu = false;
                    return;
                }
            }

            this.$emit("change", payload);
            this.rangeMenu = false;
        },
        applyRangeWithoutClosing() {
            if (!this.isRangeComplete) {
                return;
            }
            const [start, end] = this.pickerRange;
            const payload = {
                min: start,
                max: end,
            };

            // For daterange filters, check if the range is already applied in filters
            if (this.filter.type === 'daterange') {
                this.selectedPreset = null;
                const minField = `${this.filter.field}_min`;
                const maxField = `${this.filter.field}_max`;
                if (
                    this.filters[minField] === payload.min &&
                    this.filters[maxField] === payload.max
                ) {
                    // Range already applied, don't emit
                    return;
                }
            }

            this.$emit("change", payload);
            // Don't close the menu - keep it open
        },
        makeChoices(data, filter) {
            let choices = [];
            if (this.filter.defaultLabel) {
                choices.push({
                    value: null,
                    text: this.filter.defaultLabel,
                });
            }
            if (!this.filter.source) {
                data.results.forEach((item) => {
                    choices.push({
                        text: this.$utils.traverse(item, this.filter.text_field || "id"),
                        value: this.$utils.traverse(item, this.filter.value_field),
                    });
                });
                return choices;
            }
            switch (this.filter.source) {
                case "user": {
                    choices.push({
                        text: Vue.prototype.$translate('noUser'),
                        value: 'null',
                    });
                    const internalUsers = data.results.filter(
                        (item) => !item.entity || item.entity === this.current_entity_id
                    );
                    const externalUsers = data.results.filter(
                        (item) => item.entity && item.entity !== this.current_entity_id
                    );
                    internalUsers.forEach((item) => {
                        choices.push({ text: item.full_name, value: item.id });
                    });
                    if (externalUsers.length > 0) {
                        choices.push({ header: Vue.prototype.$translate('agents') });
                        externalUsers.forEach((item) => {
                            const entityName = item.entity_name || item.entity_data?.name;
                            choices.push({
                                text: entityName ? `${item.full_name} (${entityName})` : item.full_name,
                                value: item.id,
                            });
                        });
                    }
                    break;
                }
                case "catalog":
                    data.results.forEach((item) => {
                        choices.push(
                            buildCatalogChoice(item, {
                                translate: (key) => this.$translate(key),
                                currentEntityId: this.current_entity_id,
                            }),
                        );
                    });
                    break;
                case "vendor":
                    data.results.forEach((item) => {
                        choices.push({
                            text: item.entity_data?.name,
                            value: item.entity,
                        });
                    });
                    break;

                case "provider":
                case "entity":
                case "customer":
                case "buyer":
                case "initiator":
                case "recipient":
                case "deliverysite":
                case "billingaccount":
                    data.results.forEach((item) => {
                        choices.push({ text: item.name, value: item.id });
                    });
                    break;
                case "product":
                    data.results.forEach((item) => {
                        choices.push({
                            text: formatProductChoiceLabel(item),
                            value: item.id,
                        });
                    });
                    break;
                case "producttype":
                    data.results.forEach((item) => {
                        choices.push({ text: item.label, value: item.id });
                    });
                    break;
                case "stock":
                    data.results.forEach((item) => {
                        if (item.owned) {
                            choices.push({
                                value: item.id,
                                text: item.name,
                            });
                        } else {
                            choices.push({
                                value: item.id,
                                text: `${item.owner_data.name} - ${item.name}`,
                            });
                        }
                    });
                    break;

                case "stock_owner": {
                    // Same pattern as "user": primary rows first, then optional header + group.
                    choices.push({
                        text: data.entity_name,
                        value: "entity",
                    });
                    const providerItems = (data.providers || []).filter(
                        (p) => p && p.id != null,
                    );
                    if (providerItems.length > 0) {
                        choices.push({
                            header: this.$translate(
                                'inventoryOwnerSuppliers',
                            ),
                        });
                        providerItems.forEach((p) => {
                            choices.push({
                                text: p.name,
                                value: `provider_${p.id}`,
                            });
                        });
                    }
                    break;
                }

                case "productmetadata":
                    data.values.forEach((item) => {
                        choices.push({
                            text:
                                (item.text || item.value) +
                                (filter.unit ? ` ${filter.unit}` : ""),
                            value: item.value,
                        });
                    });
                    break;
                case "catalogentry_category":
                    data.forEach((item) => {
                        choices.push({
                            text: item,
                            value: item,
                        });
                    });
                    break;
                case "tags":
                    if (data.results) {
                        data.results.forEach((tag) => {
                            choices.push({
                                text: `${tag.name} (${tag.usage_count || 0})`,
                                value: tag.name,
                                color: tag.color || '#9E9E9E',
                            });
                        });
                    }
                    break;
            }
            return choices;
        },
        fetchSource() {
            console.log(this.filter);
            const utils = require("@/stores/utils");
            let params = {};
            if (this.search && this.value) {
                return;
            }
            if (this.filter.type == "searchselect") {
                params = {
                    [this.filter.searchParam || "search"]: this.search,
                    limit: 20,
                    ...this.filter.params,
                };
            } else {
                params = { ...this.filter.params, ...{ limit: 10000 } };
            }
            // For tags filter, object_type is required
            if (this.filter.source === 'tags') {
                if (!this.filter.object_type) {
                    console.error('Tags filter requires object_type to be specified. Filter:', this.filter);
                    throw new Error('Tags filter requires object_type to be specified');
                }
                params.object_type = this.filter.object_type;
            }
            this.loading = true;
            axios
                .get(this.filter.endpoint, { params })
                .then((response) => {
                    const results = response.data.results || response.data || [];
                    if (this.filter.type === "formatselect") {
                        this.formatRecords =
                            this.$utils.sortFormatsByCapacity(results);
                        this.source_choices = null;
                        if (this.value) {
                            const format = this.formatRecords.find(
                                (item) => item.id == this.value,
                            );
                            this.value_label = format
                                ? this.$utils.formatOptionLabel(format)
                                : null;
                        }
                        return;
                    }
                    this.source_choices = this.makeChoices({ ...response.data, results }, this.filter);
                })
                .catch((xhr_error) => {
                    console.error(xhr_error);
                    this.source_choices = [];
                    this.formatRecords = [];
                })
                .finally(() => {
                    this.loading = false;
                });
        },
    },
    mounted() {
        if (this.filter.endpoint) {
            this.fetchSource();
        }
        // Initialize date range from filters min/max values
        if (this.filter.type === "daterange") {
            this.initializeDateRange();
        } else if (this.filter.type === "numberrange") {
            this.initializeNumberRange();
        }
        if (this.embedded) {
            this.$nextTick(() => {
                this.openMenu();
            });
        }
    },
    i18n: {
        messages: {
            en: {
                items: "{count} values",
                noDataAvailable: "No data available",
                unnamed: "Unnamed",
                dateRangeClose: "Close",
                dateRangeApply: "Apply",
                numberRangeMin: "Min",
                numberRangeMax: "Max",
                others: "others",
                inventoryOwnerSuppliers: "Suppliers",
            },
            fr: {
                others: "autres",
                items: "{count} valeurs",
                noDataAvailable: "Aucune donnée disponible",
                unnamed: "Sans nom",
                dateRangeClose: "Fermer",
                dateRangeApply: "Appliquer",
                numberRangeMin: "Min",
                numberRangeMax: "Max",
                inventoryOwnerSuppliers: "Fournisseurs",
            },
        },
    },
};
</script>

<style lang="scss">
@import "@/assets/css/variables.scss";

.v-application .v-input--checkbox .v-input__slot.primary-lighter2 {
    border: 1px solid $color-primary !important;
}
.filter {
    i:before,
    i:after {
        color: $color-primary;
    }
    .v-input__prepend-inner .v-icon {
        color: $color-primary !important;
    }
    :not(input[type="text"]:autofocus) {
        cursor: pointer;
    }

    label,
    input[type="text"] {
        font-size: 12px;
        color: $color-gs-black !important;
    }

    fieldset {
        border: none !important;
    }
    .autocomplete {
        label,
        input[type="text"] {
            font-size: 12px;
        }
    }
    .v-input {
        padding-top: 0 !important;
    }
    .v-input__slot {
        padding: 8px;
        border-radius: $text-field-border-radius;
    }
    .v-select__selection {
        font-size: 12px;
    }
    .v-combobox__selection {
        font-size: 12px;
    }
    .v-input--selection-controls {
        padding-top: 0;
    }
    // Enhanced styling for active filters
    .active {
        fieldset {
            border: 1px solid $color-primary !important;
            border-color: $color-primary !important;
        }
    }
    &.embedded {
        .v-input__slot {
            padding: 4px 8px;
        }
    }
    .filter__date-range-presets {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        padding: 8px 12px 4px;
    }
    .filter__date-range-preset {
        border-radius: 999px;
        letter-spacing: 0;
        text-transform: none;
    }
    .filter__date-range-preset--active {
        background-color: $color-primary;
        color: #fff;
    }
    // Deep styles for CountrySelect component (when used as filter)
    .filter ::v-deep .v-input .v-combobox {
        label,
        input[type="text"],
        .v-combobox__selection {
            font-size: 12px;
            color: $color-gs-black !important;
        }
    }
}
</style>
